import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Link } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { 
  Building2, 
  FileText, 
  Plus, 
  Trash2,
  Calculator,
  ArrowLeft,
  Download,
  Send
} from "lucide-react";

const Quotation = () => {
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const clientId = searchParams.get('client_id');
  const [quotationData, setQuotationData] = useState({
    // Client Information
    clientName: "",
    clientEmail: "",
    clientPhone: "",
    clientCompany: "",
    
    // Quotation Details
    quotationNumber: `Q-${new Date().getFullYear()}-${String(Date.now()).slice(-3)}`,
    quotationDate: new Date().toISOString().split('T')[0],
    validUntil: "",
    
    // Services
    services: [
      { description: "", quantity: 1, rate: 0, amount: 0, customDescription: "" }
    ],
    
    // Additional Information
    notes: "",
    terms: "Payment terms: 50% advance, 50% upon completion\\nValidity: 30 days from quotation date\\nPrices are inclusive of VAT"
  });

  const serviceOptions = [
    "Trade License Registration - Commercial",
    "Trade License Registration - Professional", 
    "Trade License Registration - Industrial",
    "Business Setup - LLC Formation",
    "Business Setup - Free Zone Company",
    "Bank Account Opening Assistance",
    "Visa Processing Services",
    "PRO Services",
    "Office Space Solutions",
    "Accounting & Bookkeeping",
    "Legal Consultation",
    "Document Attestation",
    "Custom Service"
  ];

  // Fetch client data if client_id is provided
  useEffect(() => {
    if (clientId) {
      fetchClientData(clientId);
    }
  }, [clientId]);

  const fetchClientData = async (id: string) => {
    try {
      const response = await fetch(`https://axlxwpuaqgbfkoampsai.supabase.co/functions/v1/handle_client_management_20251222_1800?client_id=${id}`, {
        headers: {
          'Authorization': 'Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImF4bHh3cHVhcWdiZmtvYW1wc2FpIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjY0MDY3MDgsImV4cCI6MjA4MTk4MjcwOH0.kJ0f10vouM1ruwI2mwZ9tuAU_9df4l07Vzf1tE_tS6w',
          'apikey': 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImF4bHh3cHVhcWdiZmtvYW1wc2FpIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjY0MDY3MDgsImV4cCI6MjA4MTk4MjcwOH0.kJ0f10vouM1ruwI2mwZ9tuAU_9df4l07Vzf1tE_tS6w',
        }
      });

      if (response.ok) {
        const data = await response.json();
        const client = data.client;
        
        // Auto-populate client information
        setQuotationData(prev => ({
          ...prev,
          clientName: client.full_name,
          clientEmail: client.email,
          clientPhone: client.phone || '',
          clientCompany: client.company_name || ''
        }));

        toast({
          title: "Client Data Loaded",
          description: `Quotation form populated with ${client.full_name}'s information`,
        });
      }
    } catch (error) {
      console.error('Error fetching client data:', error);
      toast({
        title: "Error",
        description: "Failed to load client data",
        variant: "destructive"
      });
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setQuotationData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleServiceChange = (index: number, field: string, value: string | number) => {
    const updatedServices = [...quotationData.services];
    updatedServices[index] = {
      ...updatedServices[index],
      [field]: value
    };
    
    // Calculate amount for this service
    if (field === 'quantity' || field === 'rate') {
      updatedServices[index].amount = updatedServices[index].quantity * updatedServices[index].rate;
    }
    
    setQuotationData(prev => ({
      ...prev,
      services: updatedServices
    }));
  };

  const addService = () => {
    setQuotationData(prev => ({
      ...prev,
      services: [...prev.services, { description: "", quantity: 1, rate: 0, amount: 0, customDescription: "" }]
    }));
  };

  const removeService = (index: number) => {
    if (quotationData.services.length > 1) {
      setQuotationData(prev => ({
        ...prev,
        services: prev.services.filter((_, i) => i !== index)
      }));
    }
  };

  const calculateTotal = () => {
    return quotationData.services.reduce((total, service) => total + service.amount, 0);
  };

  const calculateVAT = () => {
    return calculateTotal() * 0.05; // 5% VAT
  };

  const calculateGrandTotal = () => {
    return calculateTotal() + calculateVAT();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!quotationData.clientName || !quotationData.clientEmail) {
      toast({
        title: "Missing Information",
        description: "Please fill in client name and email.",
        variant: "destructive"
      });
      return;
    }

    if (quotationData.services.some(service => !service.description || service.rate <= 0)) {
      toast({
        title: "Invalid Services",
        description: "Please ensure all services have descriptions and valid rates.",
        variant: "destructive"
      });
      return;
    }

    toast({
      title: "Quotation Generated Successfully!",
      description: `Quotation ${quotationData.quotationNumber} has been created and saved.`,
    });

    console.log("Quotation data:", quotationData);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="border-b bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/60">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3">
              <img 
                src="./images/logo.jpg" 
                alt="Company Logo" 
                className="h-12 w-auto object-contain"
              />
              <span className="text-2xl font-bold text-primary">Elite Edge MC</span>
            </Link>
            <div className="hidden md:flex items-center space-x-6">
              <Link to="/" className="text-foreground hover:text-primary transition-colors">Home</Link>
              <Link to="/services" className="text-foreground hover:text-primary transition-colors">Services</Link>
              <Link to="/crm" className="text-foreground hover:text-primary transition-colors">CRM</Link>
              <Button variant="outline" asChild>
                <Link to="/crm">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to CRM
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Header */}
      <section className="py-8 px-4 bg-gradient-to-br from-primary/5 via-primary/10 to-accent/5">
        <div className="container mx-auto text-center">
          <Badge className="mb-4" variant="secondary">Quotation Generator</Badge>
          <h1 className="text-4xl font-bold mb-4">Create Professional Quotation</h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Generate detailed quotations for your business consultancy services with automatic calculations and professional formatting.
          </p>
        </div>
      </section>

      {/* Quotation Form */}
      <section className="py-12 px-4">
        <div className="container mx-auto max-w-6xl">
          <form onSubmit={handleSubmit} className="space-y-8">
            
            <div className="grid lg:grid-cols-3 gap-8">
              {/* Left Column - Form */}
              <div className="lg:col-span-2 space-y-8">
                
                {/* Client Information */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <Building2 className="h-5 w-5 mr-2" />
                      Client Information
                    </CardTitle>
                    <CardDescription>
                      Enter client details for the quotation
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="clientName">Client Name *</Label>
                      <Input
                        id="clientName"
                        value={quotationData.clientName}
                        onChange={(e) => handleInputChange("clientName", e.target.value)}
                        placeholder="Enter client name"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="clientCompany">Company Name</Label>
                      <Input
                        id="clientCompany"
                        value={quotationData.clientCompany}
                        onChange={(e) => handleInputChange("clientCompany", e.target.value)}
                        placeholder="Enter company name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="clientEmail">Email Address *</Label>
                      <Input
                        id="clientEmail"
                        type="email"
                        value={quotationData.clientEmail}
                        onChange={(e) => handleInputChange("clientEmail", e.target.value)}
                        placeholder="client@example.com"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="clientPhone">Phone Number</Label>
                      <Input
                        id="clientPhone"
                        value={quotationData.clientPhone}
                        onChange={(e) => handleInputChange("clientPhone", e.target.value)}
                        placeholder="+971 XX XXX XXXX"
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Quotation Details */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <FileText className="h-5 w-5 mr-2" />
                      Quotation Details
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="grid md:grid-cols-3 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="quotationNumber">Quotation Number</Label>
                      <Input
                        id="quotationNumber"
                        value={quotationData.quotationNumber}
                        onChange={(e) => handleInputChange("quotationNumber", e.target.value)}
                        readOnly
                        className="bg-gray-50"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="quotationDate">Quotation Date</Label>
                      <Input
                        id="quotationDate"
                        type="date"
                        value={quotationData.quotationDate}
                        onChange={(e) => handleInputChange("quotationDate", e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="validUntil">Valid Until</Label>
                      <Input
                        id="validUntil"
                        type="date"
                        value={quotationData.validUntil}
                        onChange={(e) => handleInputChange("validUntil", e.target.value)}
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Services */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="flex items-center">
                          <Calculator className="h-5 w-5 mr-2" />
                          Services & Pricing
                        </CardTitle>
                        <CardDescription>
                          Add services and their pricing details
                        </CardDescription>
                      </div>
                      <Button type="button" onClick={addService} size="sm">
                        <Plus className="h-4 w-4 mr-2" />
                        Add Service
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {quotationData.services.map((service, index) => (
                      <div key={index} className="grid md:grid-cols-12 gap-4 items-end p-4 border rounded-lg">
                        <div className="md:col-span-5 space-y-2">
                          <Label>Service Description</Label>
                          <Select 
                            value={service.description} 
                            onValueChange={(value) => handleServiceChange(index, "description", value)}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select service" />
                            </SelectTrigger>
                            <SelectContent>
                              {serviceOptions.map((option) => (
                                <SelectItem key={option} value={option}>{option}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          
                          {/* Manual input field for custom service */}
                          {service.description === "Custom Service" && (
                            <div className="mt-2">
                              <Label>Custom Service Description</Label>
                              <Input
                                placeholder="Enter custom service description"
                                value={service.customDescription || ""}
                                onChange={(e) => handleServiceChange(index, "customDescription", e.target.value)}
                              />
                            </div>
                          )}
                        </div>
                        <div className="md:col-span-2 space-y-2">
                          <Label>Quantity</Label>
                          <Input
                            type="number"
                            min="1"
                            value={service.quantity}
                            onChange={(e) => handleServiceChange(index, "quantity", parseInt(e.target.value) || 1)}
                          />
                        </div>
                        <div className="md:col-span-2 space-y-2">
                          <Label>Rate (AED)</Label>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={service.rate}
                            onChange={(e) => handleServiceChange(index, "rate", parseFloat(e.target.value) || 0)}
                          />
                        </div>
                        <div className="md:col-span-2 space-y-2">
                          <Label>Amount (AED)</Label>
                          <Input
                            value={service.amount.toFixed(2)}
                            readOnly
                            className="bg-gray-50 font-semibold"
                          />
                        </div>
                        <div className="md:col-span-1">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => removeService(index)}
                            disabled={quotationData.services.length === 1}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>

                {/* Additional Information */}
                <Card>
                  <CardHeader>
                    <CardTitle>Additional Information</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="space-y-2">
                      <Label htmlFor="notes">Notes</Label>
                      <Textarea
                        id="notes"
                        value={quotationData.notes}
                        onChange={(e) => handleInputChange("notes", e.target.value)}
                        placeholder="Any additional notes or special instructions..."
                        rows={3}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="terms">Terms & Conditions</Label>
                      <Textarea
                        id="terms"
                        value={quotationData.terms}
                        onChange={(e) => handleInputChange("terms", e.target.value)}
                        rows={4}
                      />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Right Column - Preview */}
              <div className="lg:col-span-1">
                <Card className="sticky top-4">
                  <CardHeader>
                    <CardTitle>Quotation Summary</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Company Logo */}
                    <div className="text-center border-b pb-4">
                      <img 
                        src="./images/logo.jpg" 
                        alt="Company Logo" 
                        className="h-16 w-auto mx-auto mb-2"
                      />
                      <h3 className="font-bold text-lg">ConsultPro</h3>
                      <p className="text-sm text-muted-foreground">Business Consultancy Services</p>
                    </div>

                    {/* Quotation Info */}
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="font-medium">Quotation #:</span>
                        <span>{quotationData.quotationNumber}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-medium">Date:</span>
                        <span>{quotationData.quotationDate}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-medium">Client:</span>
                        <span>{quotationData.clientName || "N/A"}</span>
                      </div>
                    </div>

                    <Separator />

                    {/* Totals */}
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>Subtotal:</span>
                        <span>AED {calculateTotal().toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span>VAT (5%):</span>
                        <span>AED {calculateVAT().toFixed(2)}</span>
                      </div>
                      <Separator />
                      <div className="flex justify-between font-bold">
                        <span>Total:</span>
                        <span>AED {calculateGrandTotal().toFixed(2)}</span>
                      </div>
                    </div>

                    <Separator />

                    {/* Action Buttons */}
                    <div className="space-y-2">
                      <Button type="submit" className="w-full">
                        <FileText className="h-4 w-4 mr-2" />
                        Generate Quotation
                      </Button>
                      <Button type="button" variant="outline" className="w-full">
                        <Download className="h-4 w-4 mr-2" />
                        Download PDF
                      </Button>
                      <Button type="button" variant="outline" className="w-full">
                        <Send className="h-4 w-4 mr-2" />
                        Send to Client
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
};

export default Quotation;